import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import WorkListPage from '../../../components/work/WorkListPage';
import StandardFilterPanel from '../../../components/hr/StandardFilterPanel';
import { contractTemplatesApi } from '../../../utils/workApi';
import { parseContractTemplatesList, getContractTemplateFromResponse } from '../../../utils/workResponseUtils';
import { formatWorkPriceDisplay } from '../../../utils/workPriceUtils';
import { PANEL_PRIMARY_BTN_CLASS, BULK_ACTION_BAR_CLASS, BULK_ACTION_BAR_TEXT_CLASS } from '../../../constants/ui';
import { confirmWorkDelete } from '../../../utils/workConfirmModal';
import { Button, Table, message, Modal, Dropdown, Descriptions, Space } from 'antd';
import {
  PlusOutlined,
  ArrowRightOutlined,
  EyeOutlined,
  DeleteOutlined,
  FileProtectOutlined,
  EditOutlined,
  DownOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import { TableSkeleton } from '../../../components/SkeletonLoaders';
import { useListReturnState } from '../../../hooks/useListReturnState';

const label = (t) => <span style={{ fontFamily: 'CustomFont' }}>{t}</span>;

const resolveCurrencyLabel = (currency) =>
  currency?.currency_name || currency?.currency_symbol || '—';

const ContractTemplates = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewTpl, setViewTpl] = useState(null);
  const [viewCompany, setViewCompany] = useState(null);

  const captureCurrentListState = useCallback(
    () => ({
      searchText,
      pagination: {
        current: pagination.current,
        pageSize: pagination.pageSize,
      },
    }),
    [searchText, pagination.current, pagination.pageSize],
  );

  const { navigateToDetail } = useListReturnState(captureCurrentListState, (restored) => {
    if (restored.searchText != null) setSearchText(restored.searchText);
    if (restored.pagination) {
      setPagination((prev) => ({
        current: restored.pagination.current || 1,
        pageSize: restored.pagination.pageSize || prev.pageSize,
      }));
    }
  });

  const load = async () => {
    setLoading(true);
    try {
      const res = await contractTemplatesApi.getAll({
        page: pagination.current,
        per_page: pagination.pageSize,
        search: searchText || undefined,
      });
      const parsed = parseContractTemplatesList(res);
      setRows(parsed.rows);
      setTotal(parsed.total);
    } catch {
      message.error('خطا در بارگذاری');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination((p) => ({ ...p, current: 1 }));
      load();
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize]);

  const openView = async (rowId) => {
    setViewOpen(true);
    setViewLoading(true);
    setViewTpl(null);
    setViewCompany(null);
    try {
      const res = await contractTemplatesApi.getById(rowId);
      setViewTpl(getContractTemplateFromResponse(res));
      setViewCompany(res?.company || null);
    } catch {
      message.error('خطا در بارگذاری جزئیات');
      setViewOpen(false);
    } finally {
      setViewLoading(false);
    }
  };

  const handleBulkDelete = () => {
    confirmWorkDelete({
      title: 'حذف قالب‌ها',
      content: `آیا از حذف ${selectedRowKeys.length} قالب انتخاب‌شده اطمینان دارید؟`,
      onOk: async () => {
        setBulkLoading(true);
        try {
          await contractTemplatesApi.applyQuickAction('delete', selectedRowKeys);
          message.success('قالب‌ها حذف شدند');
          setSelectedRowKeys([]);
          load();
        } catch {
          message.error('خطا در حذف گروهی');
        } finally {
          setBulkLoading(false);
        }
      },
    });
  };

  const handleRowDelete = (rowId) => {
    confirmWorkDelete({
      title: 'حذف قالب',
      content: 'آیا از حذف این قالب اطمینان دارید؟',
      onOk: async () => {
        try {
          await contractTemplatesApi.applyQuickAction('delete', [rowId]);
          message.success('قالب حذف شد');
          load();
        } catch {
          message.error('خطا در حذف');
        }
      },
    });
  };

  const bulkActionMenuItems = [
    {
      key: 'delete',
      label: label('حذف انتخاب‌شده‌ها'),
      icon: <DeleteOutlined />,
      danger: true,
      onClick: handleBulkDelete,
    },
  ];

  const getRowActionItems = (row) => [
    {
      key: 'view',
      label: label('مشاهده'),
      icon: <EyeOutlined />,
      onClick: () =>
        navigateToDetail(`/work/contracts/templates/${row.id}`, {}, {
          listPath: '/work/contracts/templates',
          listLabel: 'قالب‌های قرارداد',
          entityLabel: row.subject || `قالب #${row.id}`,
        }),
    },
    {
      key: 'create-contract',
      label: label('ایجاد قرارداد'),
      icon: <PlusOutlined />,
      onClick: () =>
        navigate('/work/contracts/add', {
          state: {
            templateId: row.id,
            returnTo: '/work/contracts/templates',
          },
        }),
    },
    {
      key: 'edit',
      label: label('ویرایش'),
      icon: <EditOutlined />,
      onClick: () =>
        navigateToDetail(`/work/contracts/templates/${row.id}/edit`, {}, {
          listPath: '/work/contracts/templates',
          listLabel: 'قالب‌های قرارداد',
          entityLabel: row.subject ? `ویرایش ${row.subject}` : `ویرایش قالب #${row.id}`,
        }),
    },
    { type: 'divider' },
    {
      key: 'delete',
      label: label('حذف'),
      icon: <DeleteOutlined />,
      danger: true,
      onClick: () => handleRowDelete(row.id),
    },
  ];

  return (
    <WorkListPage
      title="قالب‌های قرارداد"
      subtitle="مدیریت قالب قرارداد"
      icon={FileProtectOutlined}
      headerExtra={(
        <Space wrap>
          <Button
            icon={<ArrowRightOutlined />}
            onClick={() => navigate('/work/contracts')}
            style={{ fontFamily: 'CustomFont' }}
          >
            بازگشت به قراردادها
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            className={PANEL_PRIMARY_BTN_CLASS}
            onClick={() => navigate('/work/contracts/templates/new')}
            style={{ fontFamily: 'CustomFont' }}
          >
            قالب جدید
          </Button>
        </Space>
      )}
      footer={(
        <Modal
          open={viewOpen}
          title={<span style={{ fontFamily: 'CustomFont' }}>جزئیات قالب</span>}
          onCancel={() => setViewOpen(false)}
          footer={[
            <Button key="close" onClick={() => setViewOpen(false)} style={{ fontFamily: 'CustomFont' }}>
              بستن
            </Button>,
            viewTpl?.id ? (
              <Button
                key="edit"
                type="primary"
                className={PANEL_PRIMARY_BTN_CLASS}
                icon={<EditOutlined />}
                onClick={() => {
                  setViewOpen(false);
                  navigateToDetail(`/work/contracts/templates/${viewTpl.id}/edit`);
                }}
                style={{ fontFamily: 'CustomFont' }}
              >
                ویرایش
              </Button>
            ) : null,
          ]}
          width={720}
          destroyOnClose
          styles={{ body: { fontFamily: 'CustomFont' } }}
        >
          {viewLoading ? (
            <TableSkeleton rows={4} columns={2} />
          ) : (
            <Descriptions
              bordered
              column={1}
              size="small"
              labelStyle={{ fontFamily: 'CustomFont' }}
              contentStyle={{ fontFamily: 'CustomFont' }}
            >
              <Descriptions.Item label={label('شماره')}>
                {viewTpl?.contract_template_number || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={label('موضوع')}>{viewTpl?.subject || '—'}</Descriptions.Item>
              <Descriptions.Item label={label('نوع قرارداد')}>
                {viewTpl?.contract_type?.name || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={label('مبلغ')}>
                {formatWorkPriceDisplay(viewTpl?.amount, { suffix: viewTpl?.currency?.currency_symbol })}
              </Descriptions.Item>
              <Descriptions.Item label={label('ارز')}>
                {resolveCurrencyLabel(viewTpl?.currency)}
              </Descriptions.Item>
              <Descriptions.Item label={label('شماره شرکت')}>
                {viewTpl?.company_id ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label={label('توضیحات')}>
                <span className="whitespace-pre-wrap">
                  {viewTpl?.description || viewTpl?.contract_detail || '—'}
                </span>
              </Descriptions.Item>
              {viewCompany ? (
                <>
                  <Descriptions.Item label={label('شرکت')}>{viewCompany.company_name || '—'}</Descriptions.Item>
                  <Descriptions.Item label={label('تلفن شرکت')}>
                    {viewCompany.company_phone || '—'}
                  </Descriptions.Item>
                </>
              ) : null}
            </Descriptions>
          )}
        </Modal>
      )}
    >
      <StandardFilterPanel
        searchValue={searchText}
        onSearchChange={setSearchText}
        onSearch={() => {}}
        searchPlaceholder="جستجو در قالب‌ها..."
        quickFilters={[]}
        advancedFilters={[]}
        onFilterChange={() => {}}
        onResetFilters={() => setSearchText('')}
        totalCount={total}
        showAdvancedFilters={false}
        showTotalCount
      />

      {selectedRowKeys.length > 0 ? (
        <div className={BULK_ACTION_BAR_CLASS}>
          <span className={BULK_ACTION_BAR_TEXT_CLASS} style={{ fontFamily: 'CustomFont' }}>
            {selectedRowKeys.length} مورد انتخاب شده
          </span>
          <Dropdown menu={{ items: bulkActionMenuItems }} trigger={['click']} disabled={bulkLoading}>
            <Button
              type="primary"
              loading={bulkLoading}
              icon={<DownOutlined />}
              className={PANEL_PRIMARY_BTN_CLASS}
              style={{ fontFamily: 'CustomFont' }}
            >
              عملیات گروهی
            </Button>
          </Dropdown>
          <Button type="link" onClick={() => setSelectedRowKeys([])} style={{ fontFamily: 'CustomFont' }}>
            لغو انتخاب
          </Button>
        </div>
      ) : null}

      {loading ? (
        <TableSkeleton rows={5} columns={4} />
      ) : (
        <Table
          rowKey="id"
          dataSource={rows}
          rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
          scroll={{ x: true }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total,
            showSizeChanger: true,
            onChange: (page, pageSize) => setPagination({ current: page, pageSize }),
          }}
          columns={[
            {
              title: label('شماره'),
              dataIndex: 'contract_template_number',
              width: 90,
            },
            {
              title: label('موضوع'),
              dataIndex: 'subject',
              ellipsis: true,
            },
            {
              title: label('نوع قرارداد'),
              render: (_, row) => (
                <span style={{ fontFamily: 'CustomFont' }}>{row.contract_type?.name || '—'}</span>
              ),
            },
            {
              title: label('مبلغ'),
              render: (_, row) => (
                <span style={{ fontFamily: 'CustomFont' }}>
                  {formatWorkPriceDisplay(row.amount, { suffix: row.currency?.currency_symbol })}
                </span>
              ),
            },
            {
              title: label('عملیات'),
              key: 'actions',
              fixed: 'right',
              width: 80,
              align: 'center',
              render: (_, row) => (
                <Dropdown menu={{ items: getRowActionItems(row) }} trigger={['click']}>
                  <Button
                    type="text"
                    icon={<MoreOutlined />}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Dropdown>
              ),
            },
          ]}
        />
      )}
    </WorkListPage>
  );
};

export default ContractTemplates;
